(() => {
  const SELECTORS = {
    menu: '[data-kenzu-collection-menu]',
    link: '[data-kenzu-filter-link]',
    scroller: '[data-kenzu-menu-scroller]',
    productGrid: '#ProductGridContainer',
    nativeFilters: '#main-collection-filters'
  };

  class KenzuCollectionMenu {
    constructor(element) {
      this.element = element;
      this.sectionId = element.dataset.sectionId;
      this.filterParam = element.dataset.filterParam;
      this.abortController = null;
      this.isLoading = false;

      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.element.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      this.updateButtonLinks(new URL(window.location.href));
      this.centerActiveButton(false);
    }

    onClick(event) {
      const link = event.target.closest(SELECTORS.link);

      if (!link || !this.element.contains(link)) {
        return;
      }

      const isModifiedClick =
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey;

      if (isModifiedClick) {
        return;
      }

      event.preventDefault();

      if (this.isLoading) {
        return;
      }

      const nextUrl = this.buildFilterUrl(link);

      this.loadCollection(nextUrl, true);
    }

    onPopState() {
      const currentUrl = new URL(window.location.href);

      this.loadCollection(currentUrl, false);
    }

    buildFilterUrl(link) {
      const url = new URL(window.location.href);

      const action = link.dataset.filterAction;

      const filterParam =
        link.dataset.filterParam ||
        this.filterParam;

      /*
       * Elimina únicamente la categoría seleccionada.
       * Mantiene ordenamiento, disponibilidad, precio
       * y cualquier otro parámetro existente.
       */
      url.searchParams.delete(filterParam);
      url.searchParams.delete('page');

      if (action === 'select') {
        const value = link.dataset.filterValue;

        if (value) {
          url.searchParams.set(filterParam, value);
        }
      }

      return url;
    }

    async loadCollection(url, updateHistory) {
      this.abortPendingRequest();

      this.abortController = new AbortController();
      this.setLoading(true);

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'text/html'
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `No se pudo cargar la colección: ${response.status}`
          );
        }

        const html = await response.text();

        const parser = new DOMParser();

        const nextDocument = parser.parseFromString(
          html,
          'text/html'
        );

        this.replaceProductGrid(nextDocument);
        this.replaceNativeFilters(nextDocument);

        /*
         * No reemplazamos la barra personalizada.
         * Así las categorías nunca desaparecen.
         */
        this.updateActiveButton(url);
        this.updateButtonLinks(url);

        if (updateHistory) {
          window.history.pushState(
            {
              kenzuCollectionFilter: true
            },
            '',
            url.toString()
          );
        }

        if (nextDocument.title) {
          document.title = nextDocument.title;
        }

        this.dispatchUpdateEvents();
        this.centerActiveButton(true);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error(
          '[Kenzu collection menu]',
          error
        );

        /*
         * Si AJAX falla, abre la URL normalmente.
         */
        window.location.assign(url.toString());
      } finally {
        this.setLoading(false);
      }
    }

    replaceProductGrid(nextDocument) {
      const currentGrid = document.querySelector(
        SELECTORS.productGrid
      );

      const nextGrid = nextDocument.querySelector(
        SELECTORS.productGrid
      );

      if (!currentGrid || !nextGrid) {
        throw new Error(
          'No se encontró #ProductGridContainer.'
        );
      }

      currentGrid.replaceWith(nextGrid);
    }

    replaceNativeFilters(nextDocument) {
      const currentFilters = document.querySelector(
        SELECTORS.nativeFilters
      );

      const nextFilters = nextDocument.querySelector(
        SELECTORS.nativeFilters
      );

      if (!currentFilters || !nextFilters) {
        return;
      }

      currentFilters.replaceWith(nextFilters);
    }

    updateActiveButton(url) {
      const selectedValues = url.searchParams.getAll(
        this.filterParam
      );

      const selectedValue =
        selectedValues.length > 0
          ? selectedValues[selectedValues.length - 1]
          : null;

      const buttons = this.element.querySelectorAll(
        SELECTORS.link
      );

      buttons.forEach((button) => {
        const action = button.dataset.filterAction;
        const buttonValue = button.dataset.filterValue;

        const isActive =
          action === 'clear'
            ? !selectedValue
            : buttonValue === selectedValue;

        button.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          button.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          button.removeAttribute(
            'aria-current'
          );
        }
      });
    }

    updateButtonLinks(currentUrl) {
      const buttons = this.element.querySelectorAll(
        SELECTORS.link
      );

      buttons.forEach((button) => {
        const buttonUrl = new URL(
          currentUrl.toString()
        );

        buttonUrl.searchParams.delete(
          this.filterParam
        );

        buttonUrl.searchParams.delete('page');

        if (
          button.dataset.filterAction === 'select'
        ) {
          const value = button.dataset.filterValue;

          if (value) {
            buttonUrl.searchParams.set(
              this.filterParam,
              value
            );
          }
        }

        button.href = buttonUrl.toString();
      });
    }

    centerActiveButton(animate = true) {
      requestAnimationFrame(() => {
        const scroller = this.element.querySelector(
          SELECTORS.scroller
        );

        const activeButton = this.element.querySelector(
          `${SELECTORS.link}.is-active`
        );

        if (!scroller || !activeButton) {
          return;
        }

        const targetLeft =
          activeButton.offsetLeft -
          scroller.clientWidth / 2 +
          activeButton.clientWidth / 2;

        scroller.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: animate ? 'smooth' : 'auto'
        });
      });
    }

    dispatchUpdateEvents() {
      document.dispatchEvent(
        new CustomEvent('collection:updated', {
          bubbles: true,
          detail: {
            source: 'kenzu-collection-menu'
          }
        })
      );
    }

    abortPendingRequest() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    setLoading(state) {
      this.isLoading = state;

      document.body.classList.toggle(
        'kenzu-collection-loading',
        state
      );

      this.element.classList.toggle(
        'is-loading',
        state
      );

      this.element.setAttribute(
        'aria-busy',
        String(state)
      );
    }

    destroy() {
      this.abortPendingRequest();

      this.element.removeEventListener(
        'click',
        this.onClick
      );

      window.removeEventListener(
        'popstate',
        this.onPopState
      );
    }
  }

  const instances = new WeakMap();

  function initialize(scope = document) {
    scope
      .querySelectorAll(SELECTORS.menu)
      .forEach((element) => {
        if (instances.has(element)) {
          return;
        }

        const instance =
          new KenzuCollectionMenu(element);

        instances.set(element, instance);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize()
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(event.target);
    }
  );
})();